import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { initNativePush, nativePushToken } from '../composables/useNativePush'
import { reportNativeDiag } from '../composables/useNativeSetup'
import { sfx } from '../composables/useSfx.js'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('hr_token') || '')
  const user = ref(JSON.parse(localStorage.getItem('hr_user') || 'null'))
  const bootstrapped = ref(false)

  // The httpOnly cookie can authenticate even when the localStorage token is
  // gone (iOS eviction), so `user` — not the token — is the source of truth.
  const isAuthenticated = computed(() => !!user.value)

  // Permissions: admin/super_admin hold everything; hr/manager hold what the
  // owner ticked in «الأدوار والصلاحيات» (sent by the server as user.permissions);
  // employee holds none (self-service only). The UI only hides what the server
  // would refuse anyway.
  const isStaff = computed(() => !!user.value?.role && user.value.role !== 'employee')
  const isFullRole = computed(() => ['admin', 'super_admin'].includes(user.value?.role))
  const permissionSet = computed(() => new Set(user.value?.permissions || []))
  function can(...codes) {
    return isFullRole.value || codes.some(c => permissionSet.value.has(c))
  }

  function setUser(u) {
    user.value = u
    localStorage.setItem('hr_user', JSON.stringify(u))
  }

  // Re-read the session user (role + current permissions) — the owner may have
  // changed the role's permissions since this browser cached it.
  async function refreshUser() {
    try {
      const { data } = await api.get('/auth/me', { __noAuthRedirect: true })
      if (data?.user && user.value) setUser({ ...user.value, ...data.user })   // not after a logout
    } catch (_) { /* the next real request handles an expired session */ }
  }

  // On first protected navigation, if we have no local session, try to recover
  // one from the httpOnly cookie via /me. Success → still logged in (later
  // requests authenticate through the cookie, no Bearer needed).
  //
  // Re-entrancy: concurrent navigations share ONE in-flight /me (so a second
  // route change can't read a half-set state and bounce to /login). Latching:
  // we only mark `bootstrapped` on a DEFINITIVE outcome (recovered, or an
  // explicit 401 = no session) — a transient network error stays unlatched so a
  // later navigation retries instead of stranding a cookie-valid user at login.
  let bootstrapInflight = null
  async function bootstrap() {
    if (bootstrapped.value) return
    if (user.value) {
      bootstrapped.value = true
      // A user cached before permissions existed must wait for them (the UI is
      // built from them); otherwise refresh in the background.
      if (!Array.isArray(user.value.permissions)) await refreshUser()
      else refreshUser()
      return
    }
    if (!bootstrapInflight) {
      bootstrapInflight = (async () => {
        try {
          const { data } = await api.get('/auth/me', { __noAuthRedirect: true })
          if (data?.user) {
            setUser(data.user)
            reportNativeDiag()   // cookie-recovered session runs the native init
            initNativePush()     // that main.js skips (it gates on hr_token)
          }
          bootstrapped.value = true
        } catch (e) {
          if (e?.response?.status === 401) bootstrapped.value = true
          // transient/non-401 → leave unlatched; a later navigation retries
        } finally {
          bootstrapInflight = null
        }
      })()
    }
    await bootstrapInflight
  }

  async function login(username, password, tenantSlug) {
    const payload = { username, password }
    if (tenantSlug) payload.tenant_slug = tenantSlug
    const { data } = await api.post('/auth/login', payload)
    token.value = data.token
    localStorage.setItem('hr_token', data.token)
    setUser(data.user)
    reportNativeDiag() // capture this session's env (native vs web) right after login
    initNativePush()   // iOS app: register for APNs now that we're authed (no-op in browser)
    sfx('login')       // welcome chime (the login click already armed the audio ctx)
    return data.user
  }

  async function logout() {
    try {
      // Hand this device's push targets to the logout so the server unregisters
      // them with the session — a signed-out phone (APNs/FCM token) or browser
      // (web-push subscription) must stop receiving this user's notifications.
      const body = {}
      const deviceToken = nativePushToken()
      if (deviceToken) body.device_token = deviceToken
      try {
        const reg = await navigator.serviceWorker?.getRegistration?.()
        const sub = await reg?.pushManager?.getSubscription?.()
        if (sub?.endpoint) body.push_endpoint = sub.endpoint
      } catch (_) { /* no service worker / push support */ }
      await api.post('/auth/logout', body)
    } catch (_) { /* best-effort */ }
    token.value = ''
    user.value = null
    localStorage.removeItem('hr_token')
    localStorage.removeItem('hr_user')
  }

  return { token, user, bootstrapped, isAuthenticated, isStaff, isFullRole, can, refreshUser, bootstrap, login, logout }
})
